import { type ReactNode } from "react";

import { sharedTableCommentSummaryChipSx } from "@/styles/sharedDataGridStyles";
import { Box, Chip, Tooltip, Typography, alpha, useTheme } from "@mui/material";

interface DataGridCommentTooltipProps {
	items: ReactNode[];
	count?: number;
	emptyLabel?: string;
	maxWidth?: number;
}

export const DataGridCommentTooltip = ({
	items,
	count = items.length,
	emptyLabel = "No comments",
	maxWidth = 420,
}: DataGridCommentTooltipProps) => {
	const theme = useTheme();

	if (!count) {
		return (
			<Typography variant="caption" color="text.disabled">
				{emptyLabel}
			</Typography>
		);
	}

	return (
		<Tooltip
			arrow
			slotProps={{
				tooltip: {
					sx: {
						bgcolor: "background.paper",
						color: "text.primary",
						border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
						boxShadow: theme.shadows[8],
						borderRadius: 2,
						p: 0,
						maxWidth: maxWidth + 24,
					},
				},
				arrow: {
					sx: {
						color: theme.palette.background.paper,
					},
				},
			}}
			title={
				<Box
					sx={{
						p: 1.25,
						display: "grid",
						gap: 1,
						maxWidth,
					}}
				>
					{items.map((item, index) => (
						<Box
							key={index}
							sx={{
								p: 1,
								borderRadius: 1.5,
								border: `1px solid ${alpha(theme.palette.divider, 0.75)}`,
								bgcolor:
									theme.palette.mode === "dark"
										? alpha(theme.palette.common.white, 0.03)
										: alpha(theme.palette.primary.main, 0.03),
							}}
						>
							{item}
						</Box>
					))}
				</Box>
			}
		>
			<Chip
				size="small"
				label={`${count} comment${count === 1 ? "" : "s"}`}
				sx={sharedTableCommentSummaryChipSx(theme)}
			/>
		</Tooltip>
	);
};
